import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getMeta, setMeta, META, wipeLocalMirror } from "@/lib/db";
import { getPendingCount, clearSessionExpired, markSessionExpired, syncNow } from "@/lib/sync";
import * as api from "@/lib/api";

const AuthCtx = createContext(null);

// Pide almacenamiento persistente al navegador para que iOS/Android no
// desalojen IndexedDB cuando el dispositivo necesita espacio (crítico para
// que los datos de campo sobrevivan offline por días). Tanda 3: el
// resultado ahora se guarda (antes solo se logueaba a consola) para poder
// avisarle al usuario en Ajustes si el navegador lo denegó.
async function requestPersistentStorage() {
  try {
    if (navigator.storage?.persist) {
      const granted = await navigator.storage.persist();
      await setMeta(META.STORAGE_PERSISTED, granted);
    }
  } catch (e) {
    console.warn("[storage] persist() failed", e);
    await setMeta(META.STORAGE_PERSISTED, false);
  }
}

function ownerScopeOf(u) {
  if (!u) return null;
  return u.role === "owner" ? u.id : (u.owner_id || null);
}

// Wipe selectivo (Fix hotfix 31/07/2026): el logout YA NO borra nada — solo
// guarda de quién era la sesión. Recién en el próximo login se decide si hay
// que borrar el espejo local: si es el MISMO dueño (99% de los casos, mismo
// equipo/campo), se conserva el cache y todo lo pendiente sigue ahí para
// terminar de subirse. Si entra un dueño distinto, se borra por privacidad.
export async function applyOwnerScope(newUser) {
  const newScope = ownerScopeOf(newUser);
  const lastScope = await getMeta(META.LAST_OWNER_SCOPE);
  if (lastScope && newScope && lastScope !== newScope) {
    await wipeLocalMirror();
  }
  await setMeta(META.LAST_OWNER_SCOPE, newScope);
}

// Fix I (2026-09-17): días que le quedan al token guardado, leyendo el claim
// exp del propio JWT (sin pedirle nada al servidor).
function tokenDaysLeft(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload?.exp) return null;
    return (payload.exp * 1000 - Date.now()) / 86400000;
  } catch (_) {
    return null;
  }
}

const REFRESH_THRESHOLD_DAYS = 7;

// Renovación automática al abrir la app: si al token le quedan menos de 7
// días, se pide uno nuevo y se reemplaza en meta, sin intervención del
// usuario. Así la sesión no vence en medio de una semana en el campo.
export async function maybeRefreshSession() {
  if (typeof navigator !== "undefined" && !navigator.onLine) return null;
  const token = await getMeta(META.AUTH_TOKEN);
  if (!token) return null;
  const left = tokenDaysLeft(token);
  if (left === null) return null;
  if (left <= 0) { markSessionExpired(); return null; }
  if (left > REFRESH_THRESHOLD_DAYS) return null;
  try {
    await api.refreshSession();
    return true;
  } catch (e) {
    if (e?.response?.status === 401) markSessionExpired();
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const u = await getMeta(META.CURRENT_USER);
      const t = await getMeta(META.AUTH_TOKEN);
      if (u && t) {
        setUser(u);
        requestPersistentStorage();
        // Fix I: renovación transparente antes de validar (si al token le
        // quedan menos de 7 días) — nunca borra ni bloquea nada.
        await maybeRefreshSession();
        // Validate token online (non-blocking)
        api.me().then((fresh) => {
          setMeta(META.CURRENT_USER, fresh);
          setUser(fresh);
        }).catch((e) => {
          // Sesión vencida detectada sin esperar al primer sync: el cartel
          // aparece igual. No se toca ninguna tabla local.
          if (e?.response?.status === 401) markSessionExpired();
        });
      }
      setLoading(false);
    })();
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await api.login({ email, password });
    await applyOwnerScope(data.user);
    setUser(data.user);
    requestPersistentStorage();
    return data.user;
  }, []);

  // Fix I — RE-AUTENTICACIÓN EN EL LUGAR (sesión vencida).
  // Función deliberadamente SEPARADA de login(): no llama a logout(), no llama
  // a applyOwnerScope() y no puede borrar nada. Hace exactamente tres cosas:
  // pedir el token, guardarlo en meta y arrancar el drenado.
  // La identidad se compara por OWNER SCOPE (owner_id del dueño de los datos),
  // no por email ni por usuario: si alguien escribe mal el usuario y entra con
  // otra cuenta, se devuelve needsConfirm y NO se limpia nada hasta que el
  // usuario confirme explícitamente.
  const reauth = useCallback(async (email, password, { allowOwnerSwitch = false } = {}) => {
    const data = await api.reauthRequest({ email, password });
    const newScope = ownerScopeOf(data.user);
    const prevScope = (await getMeta(META.LAST_OWNER_SCOPE)) || ownerScopeOf(user);
    const sameOwner = !prevScope || prevScope === newScope;
    if (!sameOwner && !allowOwnerSwitch) {
      return { sameOwner: false, needsConfirm: true, user: data.user, pending: await getPendingCount() };
    }
    if (!sameOwner) await wipeLocalMirror(); // otra cuenta + confirmación explícita
    await setMeta(META.AUTH_TOKEN, data.token);
    await setMeta(META.CURRENT_USER, data.user);
    await setMeta(META.LAST_OWNER_SCOPE, newScope);
    setUser(data.user);
    clearSessionExpired();
    syncNow();
    return { sameOwner, user: data.user };
  }, [user]);

  const register = useCallback(async (email, password, name, company) => {
    const data = await api.register({ email, password, name, company });
    await applyOwnerScope(data.user);
    setUser(data.user);
    requestPersistentStorage();
    return data.user;
  }, []);

  // Logout seguro (Fix 1 — hotfix pérdida de datos 31/07/2026):
  // - Si hay registros sin subir y NO hay señal: nunca se permite (throw).
  // - Si hay registros sin subir y hay señal: tampoco se permite acá — la UI
  //   (LogoutGuardModal) debe drenar la cola primero y volver a llamar acá
  //   una vez que pending === 0.
  // - force=true: escape hatch explícito (requiere que el usuario tipee
  //   "BORRAR" en la UI) — permite salir y pierde lo que no se subió.
  // - Ya NO se borra el espejo local Dexie en un logout normal: se guarda de
  //   quién era la sesión y la decisión de borrar (o no) se toma en el
  //   próximo login, según si es el mismo dueño o uno distinto.
  const logout = useCallback(async ({ force = false } = {}) => {
    if (!force) {
      const pending = await getPendingCount();
      if (pending > 0) {
        const online = typeof navigator === "undefined" || navigator.onLine;
        throw { code: "pending_sync", pending, online };
      }
    }
    if (user) {
      await setMeta(META.LAST_OWNER_SCOPE, ownerScopeOf(user));
    }
    await setMeta(META.AUTH_TOKEN, null);
    await setMeta(META.CURRENT_USER, null);
    if (force) {
      await wipeLocalMirror();
    }
    setUser(null);
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, reauth }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
